/** @format */

import { FC, useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTrackChangesState } from "../../store/useTrackChangesStore";
import { useSopStore } from "../../store/useSopStore";
import type { SopListFilters } from "../../store/useSopStore";
import { useOrgRole } from "../../hooks/useOrgRole";
import { useOrganizationStore } from "../../store/useOrganizationStore";
import { SOP } from "../../types/sop";
import Header from "../../components/ui/Header";
import HeroSection from "../../components/ui/HeroSection";
import SOPListFilters, { countFilters } from "../../components/sop/SOPListFilters";
import SOPItem from "../sop-dashboard/SOPItem";
import { MigrateSopDialog } from "../../components/sop/MigrateSopDialog";
import {
  Loader,
  FileText,
  Calendar,
  Hash,
  ClipboardCheck,
  Building2,
  User,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import "../../components/ui/Header.css";
import "../sop-dashboard/SopDashBoard.css";

const GRID_WITH_ORG = "minmax(250px, 1fr) 180px 160px 100px 170px 90px";
const DEFAULT_PAGE_SIZE = 10;

const PendingVerificationSOPs: FC = () => {
  const { canEdit } = useOrgRole();
  const { organizations, activeOrgId } = useOrganizationStore();
  const {
    fetchPendingVerificationSOPs,
    setSelectedSOP,
    sops,
    pagination,
    isLoading,
    error,
    clearError,
  } = useTrackChangesState();
  const { fetchSOPById } = useSopStore();
  const navigate = useNavigate();
  const [navigatingSopId, setNavigatingSopId] = useState<string | null>(null);
  const [migrateSop, setMigrateSop] = useState<SOP | null>(null);
  const [appliedFilters, setAppliedFilters] = useState<SopListFilters>({});
  const [pageSize] = useState(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    fetchPendingVerificationSOPs({ page: 1, limit: pageSize, ...appliedFilters });
  }, [fetchPendingVerificationSOPs, pageSize]);

  const handleApplyFilters = useCallback(
    (filters: SopListFilters) => {
      setAppliedFilters(filters);
      clearError();
      fetchPendingVerificationSOPs({ page: 1, limit: pageSize, ...filters });
    },
    [fetchPendingVerificationSOPs, clearError, pageSize],
  );

  const handleClearFilters = useCallback(() => {
    setAppliedFilters({});
    clearError();
    fetchPendingVerificationSOPs({ page: 1, limit: pageSize });
  }, [fetchPendingVerificationSOPs, clearError, pageSize]);

  const sortedSops = useMemo(() => {
    if (!sops) return [];
    return [...sops].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [sops]);

  const handleViewClick = async (sop: SOP) => {
    setNavigatingSopId(sop.sopId);
    try {
      await fetchSOPById(sop.sopId);
      setSelectedSOP(sop.sopId);
      navigate(`/sop/${sop.sopId}`, {
        state: { fromPendingVerification: true },
      });
    } finally {
      setNavigatingSopId(null);
    }
  };

  const page = pagination?.page ?? 1;
  const total = pagination?.total ?? 0;
  const limit = pagination?.limit ?? pageSize;
  const hasNext = pagination?.hasNext ?? false;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const handlePrevPage = () => {
    if (page <= 1) return;
    fetchPendingVerificationSOPs({
      page: page - 1,
      limit,
      ...appliedFilters,
    });
  };

  const handleNextPage = () => {
    if (!hasNext) return;
    fetchPendingVerificationSOPs({
      page: page + 1,
      limit,
      ...appliedFilters,
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-950 to-black text-white">
      <Header />

      <main className="pt-20 px-6 lg:px-10">
        <div className="pt-8 w-full mx-auto">
          <div className="max-w-[1440px] mx-auto">
            <HeroSection
              title="Pending Verification"
              description="Review and edit SOPs awaiting verification."
            />

            <div className="bg-gradient-to-br from-gray-900/20 to-black/40 backdrop-blur-xl border border-gray-800/15 rounded-2xl shadow-2xl shadow-black/50">
              <div className="space-y-3 mb-4">
                <SOPListFilters
                  organizations={organizations}
                  onApply={handleApplyFilters}
                  onClear={handleClearFilters}
                  activeFilterCount={countFilters(appliedFilters)}
                  disabled={isLoading}
                  showOrganizationFilter={true}
                />
                {error && (
                  <div
                    className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-300 flex items-center justify-between"
                    role="alert"
                  >
                    <span>{error}</span>
                    <button
                      type="button"
                      onClick={clearError}
                      className="ml-2 underline hover:no-underline"
                    >
                      Dismiss
                    </button>
                  </div>
                )}
              </div>
              {isLoading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="flex flex-col items-center gap-4">
                    <Loader className="animate-spin w-8 h-8 text-blue-400" />
                    <p className="text-slate-400">Loading SOPs...</p>
                  </div>
                </div>
              ) : (
                <div className="w-full">
                  <div
                    className="grid gap-4 p-4 bg-gradient-to-r from-slate-800/50 to-slate-700/50 border border-slate-600/30 rounded-t-xl"
                    style={{ gridTemplateColumns: GRID_WITH_ORG }}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="w-4 h-4 text-blue-400 shrink-0" />
                      <span className="font-semibold text-sm text-white truncate">Title</span>
                    </div>
                    <div className="flex items-center gap-2 min-w-0">
                      <User className="w-4 h-4 text-amber-400" />
                      <span className="font-semibold text-sm text-white truncate">Creator</span>
                    </div>
                    <div className="flex items-center gap-2 min-w-0">
                      <Building2 className="w-4 h-4 text-purple-400" />
                      <span className="font-semibold text-sm text-white truncate">Organization</span>
                    </div>
                    <div className="flex items-center justify-center gap-2">
                      <Hash className="w-4 h-4 text-cyan-400" />
                      <span className="font-semibold text-sm text-white">Ops</span>
                    </div>
                    <div className="flex items-center gap-2 min-w-0">
                      <Calendar className="w-4 h-4 text-green-400 shrink-0" />
                      <span className="font-semibold text-sm text-white truncate">Created</span>
                    </div>
                    <div className="flex items-center justify-center">
                      <span className="font-semibold text-sm text-white">Actions</span>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-slate-800/30 to-slate-900/30 border-l border-r border-b border-slate-600/30 rounded-b-xl overflow-hidden">
                    {sortedSops.length > 0 ? (
                      <div className="divide-y divide-slate-700/30">
                        {sortedSops.map((sop) => {
                          const displayOrgName =
                            sop.organizationName ??
                            (sop.organizationId
                              ? organizations.find(
                                (o) => o.orgId === sop.organizationId,
                              )?.name
                              : "—");
                          return (
                            <SOPItem
                              key={sop.sopId}
                              sop={sop}
                              canEdit={canEdit}
                              canEditWhenProcessing={true}
                              onViewClick={handleViewClick}
                              isNavigating={navigatingSopId === sop.sopId}
                              isSuperAdmin={true}
                              gridTemplateColumns={GRID_WITH_ORG}
                              displayOrgName={displayOrgName}
                              onMigrateClick={() => setMigrateSop(sop)}
                            />
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-16">
                        <div className="flex flex-col items-center gap-4">
                          <div className="w-16 h-16 bg-gradient-to-br from-slate-700/50 to-slate-800/50 rounded-full flex items-center justify-center border border-slate-600/30">
                            <ClipboardCheck className="w-8 h-8 text-slate-400" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-white mb-2">
                              No SOPs Pending Verification
                            </h3>
                            <p className="text-slate-400 max-w-md">
                              All SOPs have been verified or there are no SOPs
                              awaiting verification at this time.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {migrateSop && (
                <MigrateSopDialog
                  open={!!migrateSop}
                  onClose={() => setMigrateSop(null)}
                  sopId={migrateSop.sopId}
                  sopTitle={migrateSop.getDisplayTitle()}
                  currentOrgId={
                    migrateSop.organizationId ?? activeOrgId ?? undefined
                  }
                  organizations={organizations}
                  onSuccess={() =>
                    fetchPendingVerificationSOPs({
                      page: 1,
                      limit,
                      ...appliedFilters,
                    })
                  }
                />
              )}

              {totalPages > 1 && (
                <div className="flex items-center justify-between gap-4 px-4 py-3 border-t border-slate-600/30 bg-slate-800/20">
                  <p className="text-slate-400 text-sm">
                    Page {page} of {totalPages}
                    {total > 0 && <span className="ml-2">({total} total)</span>}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handlePrevPage}
                      disabled={page <= 1 || isLoading}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-600/50 bg-slate-700/30 text-slate-300 hover:bg-slate-600/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </button>
                    <button
                      type="button"
                      onClick={handleNextPage}
                      disabled={!hasNext || isLoading}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-600/50 bg-slate-700/30 text-slate-300 hover:bg-slate-600/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PendingVerificationSOPs;
